import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchSingleAppDetails, fetchRelatedApps, trackAppDownload } from "../api/GlobalApi";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Constrants from "../constrants/constrant";
import { useToast } from "../context/ToastProvider";
import ImageLightbox from "../components/ImageLightbox";
import VersionHistory from "../components/version/VersionHistory";
import ReleaseNotes from "../components/version/ReleaseNotes";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import { useVersionManagement } from "../hooks/useVersionManagement";


function SingleApp() {
    const navigate = useNavigate();
    const { slug } = useParams();
    const [app, setApp] = useState(null);
    const [relatedApps, setRelatedApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [isAndroid, setIsAndroid] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [userReview, setUserReview] = useState({
        rating: 0,
        comment: ""
    });
    const [reportData, setReportData] = useState({
        reason: "",
        description: ""
    });
    const [downloading, setDownloading] = useState(false);

    const { addToast } = useToast();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userReviewExists, setUserReviewExists] = useState(false);

    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [isInWishlist, setIsInWishlist] = useState(false);
    const [hasPurchased, setHasPurchased] = useState(false);
    const [isPaidApp, setIsPaidApp] = useState(false);
    const [appPrice, setAppPrice] = useState(0);

    const [ratingDistribution, setRatingDistribution] = useState({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });

    // Version management hook
    const {
        versions,
        latestVersion,
        loading: versionsLoading,
        error: versionsError,
        fetchVersions,
        handleDownload: handleVersionDownload,
        retry: retryVersions,
        downloadProgress
    } = useVersionManagement(app?.id, { autoFetch: false });

    // Check login status once on mount
    useEffect(() => {
        const token = localStorage.getItem("token");
        setIsLoggedIn(!!token);
    }, []);

    // Check if app is paid and set price
    useEffect(() => {
        if (!app) return;
        const price = parseFloat(app.price) || 0;
        setAppPrice(price);
        setIsPaidApp(price > 0);
    }, [app]);

    // Fetch user-specific data in parallel (non-blocking)
    useEffect(() => {
        if (!isLoggedIn || !app?.id) return;

        // Fetch all user-specific data in parallel without blocking UI
        const fetchUserData = async () => {
            const token = localStorage.getItem("token");
            const headers = { Authorization: `Bearer ${token}` };

            // Run all user-specific requests in parallel
            Promise.all([
                Constrants.get(`/apps/${app.id}/my-review`, { headers }).catch(() => null),
                Constrants.get(`/apps/${app.id}/wishlist/check`, { headers }).catch(() => null),
                Constrants.get(`/apps/${app.id}/check-purchase`, { headers }).catch(() => null)
            ]).then(([reviewRes, wishlistRes, purchaseRes]) => {
                // Handle review data
                if (reviewRes?.data?.review) {
                    setUserReview({
                        rating: reviewRes.data.review.rating,
                        comment: reviewRes.data.review.comment || ""
                    });
                    setUserReviewExists(true);
                }

                // Handle wishlist data
                if (wishlistRes?.data) {
                    setIsInWishlist(wishlistRes.data.in_wishlist);
                }

                // Handle purchase data
                if (purchaseRes?.data) {
                    setHasPurchased(purchaseRes.data.has_purchased);
                }
            }).catch(error => {
                console.error("Error fetching user data:", error);
            });
        };

        fetchUserData();
    }, [isLoggedIn, app?.id]);

    // Fetch reviews (lazy load - only when needed)
    const fetchReviews = async () => {
        if (!app?.id || reviews.length > 0) return; // Don't refetch if already loaded

        setReviewsLoading(true);
        try {
            const res = await Constrants.get(`/apps/${app.id}/reviews`);
            const reviewsData = res.data.data || res.data;
            setReviews(reviewsData);

            // Calculate rating distribution
            const distribution = calculateRatingDistribution(reviewsData);
            setRatingDistribution(distribution);
        } catch (error) {
            console.error("Error fetching reviews:", error);
        } finally {
            setReviewsLoading(false);
        }
    };

    // Lazy load reviews immediately (visible on page)
    useEffect(() => {
        if (!app?.id) return;

        // Load reviews immediately as they're visible
        fetchReviews().catch(error => {
            console.error("Error loading reviews:", error);
        });
    }, [app?.id]);

    // Lazy load versions only when needed (below the fold)
    useEffect(() => {
        if (!app?.id) return;

        // Delay version loading significantly as it's below the fold
        const timer = setTimeout(() => {
            fetchVersions().catch(error => {
                console.error("Error loading versions:", error);
            });
        }, 500); // Longer delay for below-the-fold content

        return () => clearTimeout(timer);
    }, [app?.id]);

    useEffect(() => {
        if (!slug) return;

        // Scroll to top when page loads
        window.scrollTo(0, 0);

        // Detect Android immediately (synchronous)
        const ua = navigator.userAgent.toLowerCase();
        setIsAndroid(ua.includes("android"));

        const loadApp = async () => {
            setLoading(true);
            try {
                // Fetch app details with minimal processing
                const response = await fetchSingleAppDetails(slug);
                const appData = response.data?.data || response.data || response;

                // Set app data and stop loading immediately
                setApp(appData);
                setLoading(false);

                // Fetch related apps in background (completely non-blocking)
                // Use setTimeout to ensure it doesn't block the render
                if (appData.category_id) {
                    setTimeout(() => {
                        fetchRelatedApps(appData.category_id, appData.id)
                            .then(related => setRelatedApps(related || []))
                            .catch(() => setRelatedApps([]));
                    }, 200); // Delay to prioritize main content
                }

            } catch (error) {
                console.error("Error fetching app:", error);
                setLoading(false);
            }
        };

        loadApp();
    }, [slug]);

    // Memoize helper functions to prevent recreation on every render
    const formatFileSize = useCallback((bytes) => {
        if (!bytes) return "Unknown";
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(2) + " " + sizes[i];
    }, []);

    const formatNumber = useCallback((num) => {
        if (!num || num === 0) return "0";
        if (num >= 1000000) return (num / 1000000).toFixed(1) + "M+";
        if (num >= 1000) return (num / 1000).toFixed(1) + "K+";
        return num.toString();
    }, []);

    const getStars = useCallback((rating) => {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        let stars = "★".repeat(fullStars);
        if (hasHalfStar) stars += "★";
        stars += "☆".repeat(5 - Math.ceil(rating));
        return stars;
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    }, []);

    const calculateRatingDistribution = useCallback((reviews) => {
        const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

        reviews.forEach(review => {
            const rating = Math.round(review.rating);
            if (rating >= 1 && rating <= 5) {
                distribution[rating]++;
            }
        });

        return distribution;
    }, []);

    async function handleInstall() {
        // Check if user is logged in
        if (!isLoggedIn) {
            addToast("Please login to download this app", "error");
            navigate("/login");
            return;
        }

        if (!app?.id) {
            addToast("App information not available", "error");
            return;
        }

        // Check if app is paid and user has purchased it
        if (isPaidApp && !hasPurchased) {
            addToast("Please purchase this app before downloading", "error");
            return;
        }

        setDownloading(true);

        // Check if app has file_url (old system) or use version management (new system)
        const hasOldFileUrl = app.file_url && app.file_url.trim() !== '';

        try {
            // Try version management download first (new system)
            const result = await handleVersionDownload();

        } catch (error) {
            console.error("Download error:", error);
            addToast("Download failed. Please try again.", "error");
        } finally {
            setTimeout(() => {
                setDownloading(false);
            }, 2000);
        }
    }

    async function handlePurchase() {
        if (!isLoggedIn) {
            addToast("Please login to purchase this app", "error");
            navigate("/login");
            return;
        }

        try {
            // Create Razorpay order
            const token = localStorage.getItem("token");
            const orderRes = await Constrants.post(`/apps/${app.id}/create-order`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!orderRes.data) {
                throw new Error("Failed to create order");
            }

            const { order_id, amount, currency, key, app: appData, user } = orderRes.data;

            // Razorpay Checkout options
            const options = {
                key: key, // Razorpay Key ID from backend
                amount: amount * 100, // Amount in paise
                currency: currency,
                name: "App Store",
                description: `Purchase ${appData.name}`,
                image: appData.icon ? (appData.icon.startsWith('http') ? appData.icon : `${Constrants.defaults.baseURL.replace('/api', '')}/${appData.icon}`) : "",
                order_id: order_id,
                handler: async function (response) {
                    // Payment successful, verify on backend
                    try {
                        const verifyRes = await Constrants.post(`/apps/${app.id}/verify-payment`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        }, {
                            headers: { Authorization: `Bearer ${token}` }
                        });

                        if (verifyRes.data) {
                            addToast("Payment successful! You can now download the app.", "success");
                            setHasPurchased(true);
                        }
                    } catch (error) {
                        console.error("Payment verification failed:", error);
                        addToast("Payment verification failed. Please contact support.", "error");
                    }
                },
                prefill: {
                    name: user.name,
                    email: user.email,
                },
                theme: {
                    color: "#3399cc"
                },
                modal: {
                    ondismiss: function () {
                        addToast("Payment cancelled", "info");
                    }
                }
            };

            // Open Razorpay Checkout
            const razorpay = new window.Razorpay(options);
            razorpay.open();

        } catch (error) {
            console.error("Purchase error:", error);
            if (error.response?.data?.message) {
                addToast(error.response.data.message, "error");
            } else {
                addToast("Failed to initiate purchase. Please try again.", "error");
            }
        }
    }

    const handleReviewSubmit = async (e) => {
        e.preventDefault();

        if (!isLoggedIn) {
            addToast("Please login to write a review", "error");
            navigate("/login");
            return;
        }

        if (userReview.rating === 0) {
            addToast("Please select a rating", "error");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const res = await Constrants.post(
                `/apps/${app.id}/review`,
                {
                    rating: userReview.rating,
                    comment: userReview.comment
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            addToast(res.data.message, "success");
            setShowReviewModal(false);
            setUserReviewExists(true);
            fetchReviews(); // Add this line to refresh reviews
        } catch (error) {
            console.error("Error submitting review:", error);
            addToast(
                error.response?.data?.message || "Failed to submit review",
                "error"
            );
        }
    };

    const handleReportSubmit = async (e) => {
        e.preventDefault();

        if (!isLoggedIn) {
            addToast("Please login to report this app", "error");
            navigate("/login");
            return;
        }

        if (!reportData.reason) {
            addToast("Please select a reason", "error");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const res = await Constrants.post(
                `/apps/${app.id}/report`,
                {
                    reason: reportData.reason,
                    description: reportData.description
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            addToast(res.data.message, "success");
            setShowReportModal(false);
            setReportData({ reason: "", description: "" });
        } catch (error) {
            console.error("Error submitting report:", error);
            addToast(
                error.response?.data?.message || "Failed to submit report",
                "error"
            );
        }
    };

    const handleWishlistToggle = async () => {
        if (!isLoggedIn) {
            addToast("Please login to add to wishlist", "error");
            navigate("/login");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const res = await Constrants.post(
                `/apps/${app.id}/wishlist/toggle`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setIsInWishlist(res.data.in_wishlist);
            addToast(res.data.message, "success");
        } catch (error) {
            console.error("Error toggling wishlist:", error);
            addToast("Failed to update wishlist", "error");
        }
    };

    const openLightbox = (index) => {
        setCurrentImageIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
    };

    const nextImage = () => {
        if (currentImageIndex < app.screenshots_urls.length - 1) {
            setCurrentImageIndex(currentImageIndex + 1);
        }
    };

    const prevImage = () => {
        if (currentImageIndex > 0) {
            setCurrentImageIndex(currentImageIndex - 1);
        }
    };

    const handleShare = async () => {
        const shareData = {
            title: app.name,
            text: `Check out ${app.name} by ${app.developer || 'Unknown Developer'}`,
            url: window.location.href
        };

        try {
            // Check if Web Share API is supported
            if (navigator.share) {
                await navigator.share(shareData);
                addToast("Shared successfully!", "success");
            } else {
                // Fallback: Copy link to clipboard
                await navigator.clipboard.writeText(window.location.href);
                addToast("Link copied to clipboard!", "success");
            }
        } catch (error) {
            // User cancelled share or error occurred
            if (error.name !== 'AbortError') {
                console.error("Error sharing:", error);
                // Try clipboard as final fallback
                try {
                    await navigator.clipboard.writeText(window.location.href);
                    addToast("Link copied to clipboard!", "success");
                } catch (clipboardError) {
                    addToast("Failed to share. Please copy the URL manually.", "error");
                }
            }
        }
    };


    // Memoize expensive calculations (must be before early returns)
    const rating = useMemo(() => app ? parseFloat(app.rating) || 0 : 0, [app?.rating]);
    const ratingCount = useMemo(() => app ? app.rating_count || 0 : 0, [app?.rating_count]);
    const downloadsCount = useMemo(() => app ? app.downloads_count || 0 : 0, [app?.downloads_count]);

    if (loading) {
        return (
            <main className="main-content">
                <LoadingSpinner fullPage={true} size="large" message="Loading app details..." />
            </main>
        );
    }

    if (!app) {
        return (
            <main className="main-content">
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#666'
                }}>
                    <h2>App Not Found</h2>
                    <p>The app you're looking for doesn't exist or has been removed.</p>
                    <Link
                        to="/"
                        style={{
                            display: 'inline-block',
                            marginTop: '20px',
                            padding: '10px 24px',
                            background: '#1a73e8',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px'
                        }}
                    >
                        Go to Home
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main className="main-content">
            {app && (
                <Helmet>
                    <title>{app.name} - Play Store</title>
                    <link rel="icon" href={app.icon_url} />
                    <meta name="description" content={app.description} />
                    <meta name="keywords" content={app.name + ", " + app.category + ", " + app.developer} />
                    <meta property="og:title" content={app.name + " - Play Store"} />
                    <meta property="og:description" content={app.description} />
                    <meta property="og:image" content={app.icon_url} />
                    <meta property="og:url" content={window.location.href} />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={app.name + " - Play Store"} />
                    <meta name="twitter:description" content={app.description} />
                    <meta name="twitter:image" content={app.icon_url} />
                </Helmet>
            )}
            <div className="breadcrumb">
                <Link to={'/'}>Home</Link>
                <span>›</span>
                <Link to={`/apps/category/${app.category_slug}`}>{app.category || "Uncategorized"}</Link>
                <span>›</span>
                <span>{app.name}</span>
            </div>

            <section className="app-header">
                <div className="app-icon-large">
                    <img
                        src={app.icon_url}
                        style={{ width: "inherit", height: "inherit" }}
                        alt={app.name}
                    />
                </div>
                <div className="app-info">
                    <h1 className="app-title">{app.name}</h1>
                    <Link to={`/developer/${app.developer_slug}`} className="app-developer" style={{ textDecoration: "none" }}>{app.developer || "Unknown Developer"}</Link>
                    <div className="app-stats">
                        <div className="stat-item">
                            <div className="rating-stars">
                                <span className="stars">{getStars(rating)}</span>
                                <span className="rating-text">{rating.toFixed(1)}</span>
                            </div>
                            <div className="stat-label">
                                {ratingCount > 0 ? `${formatNumber(ratingCount)} reviews` : "Reviews"}
                            </div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-value">{formatNumber(downloadsCount)}</div>
                            <div className="stat-label">Downloads</div>
                        </div>
                        <div className="stat-item">
                            <div className="content-rating">{app.content_rating || "Everyone"}</div>
                            <div className="stat-label">Content Rating</div>
                        </div>
                    </div>
                    <div className="download-section">
                        <div className="install-share-wishlist" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' }}>
                            {isAndroid ? (
                                <div style={{ position: 'relative', display: 'inline-block', flex: '1', minWidth: '0' }}>
                                    {!isLoggedIn ? (
                                        <button
                                            className="install-btn"
                                            onClick={() => navigate("/login")}
                                            style={{ width: '100%' }}
                                        >
                                            <span className="btn-text-desktop">Login to Download</span>
                                            <span className="btn-text-mobile">Login</span>
                                        </button>
                                    ) : isPaidApp && !hasPurchased ? (
                                        <button
                                            className="install-btn"
                                            onClick={handlePurchase}
                                            style={{
                                                width: '100%',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                            }}
                                        >
                                            <span className="btn-text-desktop">Buy Now - ${appPrice.toFixed(2)}</span>
                                            <span className="btn-text-mobile">Buy</span>
                                        </button>
                                    ) : (
                                        <button
                                            className="install-btn"
                                            id="installBtn"
                                            onClick={handleInstall}
                                            disabled={downloading}
                                            style={{
                                                width: '100%',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {downloading && downloadProgress > 0 && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    width: `${downloadProgress}%`,
                                                    backgroundColor: 'rgba(76, 175, 80, 0.3)',
                                                    transition: 'width 0.3s ease',
                                                    zIndex: 0
                                                }} />
                                            )}
                                            <span style={{ position: 'relative', zIndex: 1 }}>
                                                {downloading ? (
                                                    <>
                                                        <svg
                                                            style={{
                                                                animation: 'spin 1s linear infinite',
                                                                marginRight: '8px',
                                                                display: 'inline-block',
                                                                verticalAlign: 'middle'
                                                            }}
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                        >
                                                            <circle cx="12" cy="12" r="10" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                                                        </svg>
                                                        <span className="btn-text-desktop">Downloading {downloadProgress}%</span>
                                                        <span className="btn-text-mobile">{downloadProgress}%</span>
                                                    </>
                                                ) : "Install"}
                                            </span>
                                        </button>
                                    )}
                                    <style>{`
                                        @keyframes spin {
                                            from { transform: rotate(0deg); }
                                            to { transform: rotate(360deg); }
                                        }
                                        
                                        .btn-text-mobile {
                                            display: none;
                                        }
                                        
                                        @media (max-width: 600px) {
                                            .btn-text-desktop {
                                                display: none;
                                            }
                                            .btn-text-mobile {
                                                display: inline;
                                            }
                                        }
                                    `}</style>
                                </div>
                            ) : (
                                <p style={{ color: "red", fontWeight: "bold", margin: 0 }}>
                                    This device is not supported
                                </p>
                            )}
                            <button
                                className="wishlist-btn"
                                title={isInWishlist ? "Remove from Wishlist" : "Add to Wishlist"}
                                onClick={handleWishlistToggle}
                                style={{
                                    color: isInWishlist ? "#d32f2f" : "inherit",
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 16px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    background: 'white',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill={isInWishlist ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                                    <path d="M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5 2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z" />
                                </svg>
                                <span className="btn-text-desktop">Add to Wishlist</span>
                            </button>
                            <button
                                className="share-btn"
                                title="Share"
                                onClick={handleShare}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 16px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    background: 'white',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18,16.08C17.24,16.08 16.56,16.38 16.04,16.85L8.91,12.7C8.96,12.47 9,12.24 9,12C9,11.76 8.96,11.53 8.91,11.3L15.96,7.19C16.5,7.69 17.21,8 18,8A3,3 0 0,0 21,5A3,3 0 0,0 18,2A3,3 0 0,0 15,5C15,5.24 15.04,5.47 15.09,5.7L8.04,9.81C7.5,9.31 6.79,9 6,9A3,3 0 0,0 3,12A3,3 0 0,0 6,15C6.79,15 7.5,14.69 8.04,14.19L15.16,18.34C15.11,18.55 15.08,18.77 15.08,19C15.08,20.61 16.39,21.91 18,21.91C19.61,21.91 20.92,20.61 20.92,19A2.92,2.92 0 0,0 18,16.08Z" />
                                </svg>
                                <span className="btn-text-desktop">Share</span>
                            </button>
                        </div>
                        {/* <button
                            className="share-btn"
                            title="Report this app"
                            onClick={() => setShowReportModal(true)}
                            style={{ marginLeft: "8px" }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
                            </svg>
                        </button> */}
                    </div>
                </div>
            </section>

            <section className="screenshots-section">
                <h2 className="section-title">Screenshots</h2>
                <div className="screenshots-container">
                    {app.screenshots_urls && app.screenshots_urls.length > 0 ? (
                        app.screenshots_urls.map((url, index) => (
                            <div
                                key={index}
                                className="screenshot"
                                onClick={() => openLightbox(index)}
                                style={{ cursor: 'pointer' }}
                            >
                                <img
                                    src={url}
                                    alt={`Screenshot ${index + 1}`}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: "12px",
                                        transition: "transform 0.2s"
                                    }}
                                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                />
                            </div>
                        ))
                    ) : (
                        <p>No screenshots available</p>
                    )}
                </div>
            </section>

            <section className="about-section">
                <h2 className="section-title">About this {app.category === "Games" ? "game" : "app"}</h2>
                <div
                    className="app-description"
                    dangerouslySetInnerHTML={{
                        __html: (app.description || "No description available")
                            .replace(/\n/g, "<br>")
                            .replace(/•/g, "<br>• ")
                    }}
                />
            </section>

            {/* What's New Section - Display latest version info */}
            {(latestVersion?.release_notes || app.release_notes) && (
                <section className="about-section">
                    <h2 className="section-title">What's New</h2>
                    <div style={{ marginBottom: '12px' }}>
                        <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#2c3e50'
                        }}>
                            Version {latestVersion?.version || app.version}
                        </span>
                        {latestVersion?.version_code && (
                            <span style={{
                                fontSize: '14px',
                                color: '#7f8c8d',
                                marginLeft: '8px'
                            }}>
                                ({latestVersion.version_code})
                            </span>
                        )}
                    </div>
                    <ReleaseNotes
                        notes={latestVersion?.release_notes || app.release_notes}
                        maxLength={300}
                    />
                </section>
            )}

            <div className="app-info-grid">
                <div className="info-section">
                    <h3>Additional Information</h3>
                    <div className="info-item">
                        <span className="info-label">Published On</span>
                        <span className="info-value">{formatDate(app.approved_at)}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Updated</span>
                        <span className="info-value">{formatDate(app.updated_at)}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Size</span>
                        <span className="info-value">{formatFileSize(app.file_size)}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Installs</span>
                        <span className="info-value">{formatNumber(downloadsCount)}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Current Version</span>
                        <span className="info-value">{app.version || "N/A"}</span>
                    </div>
                    {app.minimum_sdk && (
                        <div className="info-item">
                            <span className="info-label">Minimum SDK</span>
                            <span className="info-value">{app.minimum_sdk}</span>
                        </div>
                    )}
                    {app.target_sdk && (
                        <div className="info-item">
                            <span className="info-label">Target SDK</span>
                            <span className="info-value">{app.target_sdk}</span>
                        </div>
                    )}
                    <div className="info-item">
                        <span className="info-label">Content Rating</span>
                        <span className="info-value">{app.content_rating || "Everyone"}</span>
                    </div>
                    {app.contains_ads === 1 && (
                        <div className="info-item">
                            <span className="info-label">Contains Ads</span>
                            <span className="info-value">Yes</span>
                        </div>
                    )}
                    {app.in_app_purchases && (
                        <div className="info-item">
                            <span className="info-label">In-app Products</span>
                            <span className="info-value">{app.in_app_purchases}</span>
                        </div>
                    )}
                </div>

                <div className="info-section">
                    <h3>Developer Contact</h3>
                    <div className="info-item">
                        <span className="info-label">Developer</span>
                        <span className="info-value">{app.developer || "Unknown"}</span>
                    </div>
                    {app.support_email && (
                        <div className="info-item">
                            <span className="info-label">Email</span>
                            <span className="info-value">
                                <a href={`mailto:${app.support_email}`} style={{ color: "#1a73e8" }}>
                                    {app.support_email}
                                </a>
                            </span>
                        </div>
                    )}
                    {app.developer_website && (
                        <div className="info-item">
                            <span className="info-label">Website</span>
                            <span className="info-value">
                                <a href={app.developer_website} target="_blank" rel="noopener noreferrer" style={{ color: "#1a73e8" }}>
                                    Visit Website
                                </a>
                            </span>
                        </div>
                    )}
                    {app.support_phone && (
                        <div className="info-item">
                            <span className="info-label">Phone</span>
                            <span className="info-value">{app.support_phone}</span>
                        </div>
                    )}
                    {app.privacy_policy_url && (
                        <div className="info-item">
                            <span className="info-label">Privacy Policy</span>
                            <span className="info-value">
                                <a href={app.privacy_policy_url} target="_blank" rel="noopener noreferrer" style={{ color: "#1a73e8" }}>
                                    View Policy
                                </a>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <section className="reviews-section">
                <div className="reviews-header">
                    <h2 className="section-title">Ratings and reviews</h2>
                    <button
                        className="write-review-btn"
                        onClick={() => {
                            if (!isLoggedIn) {
                                addToast("Please login to write a review", "error");
                                navigate("/login");
                                return;
                            }
                            setShowReviewModal(true);
                        }}
                    >
                        {userReviewExists ? "Edit Review" : "Write a Review"}
                    </button>
                </div>

                <div className="rating-breakdown">
                    <div className="overall-rating">
                        <div className="overall-score">{rating.toFixed(1)}</div>
                        <div className="overall-stars">{getStars(rating)}</div>
                        <div className="total-reviews">
                            {reviews.length > 0 ? `${reviews.length} review${reviews.length !== 1 ? 's' : ''}` : "No reviews yet"}
                        </div>
                    </div>
                    <div className="rating-bars">
                        {[5, 4, 3, 2, 1].map((star) => {
                            const count = ratingDistribution[star] || 0;
                            const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;

                            return (
                                <div className="rating-bar" key={star}>
                                    <span className="bar-label">{star}</span>
                                    <div className="bar-track">
                                        <div
                                            className="bar-fill"
                                            style={{
                                                width: `${percentage}%`,
                                                transition: 'width 0.3s ease'
                                            }}
                                        ></div>
                                    </div>
                                    <span style={{
                                        fontSize: '12px',
                                        color: '#666',
                                        marginLeft: '8px',
                                        minWidth: '30px'
                                    }}>
                                        {count}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Reviews List */}
                <div style={{ marginTop: "30px" }}>
                    {reviewsLoading ? (
                        <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                            Loading reviews...
                        </div>
                    ) : reviews.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "#666" }}>
                            <p>No reviews available yet. Be the first to review!</p>
                        </div>
                    ) : (
                        reviews.map((review) => (
                            <div
                                key={review.id}
                                style={{
                                    padding: "20px",
                                    borderBottom: "1px solid #e0e0e0",
                                    marginBottom: "10px"
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                    <img
                                        src={review.user?.avatar || "/default-avatar.png"}
                                        alt={review.user?.name || "User"}
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "50%",
                                            marginRight: "12px",
                                            objectFit: "cover"
                                        }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: "500", fontSize: "14px" }}>
                                            {review.user?.name || "Anonymous"}
                                        </div>
                                        <div style={{ color: "#ffa000", fontSize: "16px" }}>
                                            {getStars(review.rating)}
                                        </div>
                                    </div>
                                    <div style={{ marginLeft: "auto", fontSize: "12px", color: "#666" }}>
                                        {formatDate(review.created_at)}
                                    </div>
                                </div>
                                {review.comment && (
                                    <div style={{
                                        fontSize: "14px",
                                        color: "#333",
                                        lineHeight: "1.5",
                                        marginLeft: "52px"
                                    }}>
                                        {review.comment}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </section>

            {/* Version History Section */}
            <section className="about-section" style={{ marginTop: '40px' }}>
                {versionsLoading && !versions.length ? (
                    <LoadingSpinner size="medium" message="Loading version history..." />
                ) : versionsError && !versions.length ? (
                    <ErrorMessage
                        message={versionsError}
                        onRetry={retryVersions}
                        fullWidth={true}
                    />
                ) : versions && versions.length > 0 ? (
                    <VersionHistory
                        versions={versions}
                        onDownload={handleVersionDownload}
                        initialDisplayCount={5}
                        showLoadMore={true}
                    />
                ) : null}
            </section>

            <div>
                <button title="Report this app" onClick={() => setShowReportModal(true)} style={{ display: "flex", alignItems: "center", gap: "10px", background: "none", border: "none", padding: "8px 0px", borderRadius: "4px", cursor: "pointer", fontSize: "14px", marginRight: "10px" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M200-120v-680h360l16 80h224v400H520l-16-80H280v280h-80Zm300-440Zm86 160h134v-240H510l-16-80H280v240h290l16 80Z" /></svg>
                    Flag as inappropriate
                </button>
            </div>

            <section className="similar-app">
                <h2 className="section-title">Similar {app.category === "Games" ? "games" : "apps"}</h2>
                <div className="apps-grid">
                    {/* Similar Apps */}
                    {relatedApps && relatedApps.length > 0 ? (
                        relatedApps.slice(0, 8).map((related) => {
                            const rating = parseFloat(related.rating) || 0;
                            const fullStars = Math.floor(rating);

                            return (
                                <Link
                                    to={`/app/details/${related.slug}`}
                                    key={related.id}
                                    className="app-card"
                                    style={{
                                        textDecoration: "none",
                                        opacity: "1",
                                        transform: "translateY(0px)",
                                        transition: "0.5s"
                                    }}
                                    onClick={() => {
                                        // Scroll to top when clicking similar app
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    title={`${related.name} - ${related.developer}`}
                                >
                                    <div className="app-card-content">
                                        <div
                                            className="app-card-icon"
                                            style={{
                                                backgroundImage: `url(${related.icon_url})`,
                                                backgroundSize: "cover",
                                                backgroundPosition: "center",
                                                borderRadius: "12px",
                                                width: "60px",
                                                height: "60px"
                                            }}
                                            role="img"
                                            aria-label={`${related.name} icon`}
                                        ></div>
                                        <div className="app-card-info">
                                            <div className="app-card-title">{related.name}</div>
                                            <div className="app-card-developer">{related.developer}</div>
                                            <div className="app-card-rating">
                                                <span className="app-card-stars">
                                                    {"★".repeat(fullStars) + "☆".repeat(5 - fullStars)}
                                                </span>
                                                <span className="app-card-rating-text">{rating.toFixed(1)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })
                    ) : (
                        <p style={{ color: '#666', padding: '20px 0' }}>No similar apps available</p>
                    )}
                </div>

            </section>

            {/* Review Modal */}
            {showReviewModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: "white",
                        padding: "30px",
                        borderRadius: "8px",
                        width: "90%",
                        maxWidth: "500px",
                        maxHeight: "90vh",
                        overflow: "auto"
                    }}>
                        <h2 style={{ marginTop: 0 }}>Write a Review</h2>
                        <form onSubmit={handleReviewSubmit}>
                            <div style={{ marginBottom: "20px" }}>
                                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                                    Rating *
                                </label>
                                <div style={{ fontSize: "32px", cursor: "pointer" }}>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <span
                                            key={star}
                                            onClick={() => setUserReview({ ...userReview, rating: star })}
                                            style={{ color: star <= userReview.rating ? "#ffa000" : "#ccc" }}
                                        >
                                            ★
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div style={{ marginBottom: "20px" }}>
                                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                                    Your Review
                                </label>
                                <textarea
                                    value={userReview.comment}
                                    onChange={(e) => setUserReview({ ...userReview, comment: e.target.value })}
                                    placeholder="Share your experience with this app..."
                                    style={{
                                        width: "100%",
                                        minHeight: "120px",
                                        padding: "10px",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        fontSize: "14px",
                                        fontFamily: "inherit",
                                        resize: "vertical"
                                    }}
                                />
                            </div>

                            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowReviewModal(false);
                                        setUserReview({ rating: 0, comment: "" });
                                    }}
                                    style={{
                                        padding: "10px 20px",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        background: "white",
                                        cursor: "pointer"
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="primary-btn"
                                >
                                    Submit Review
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Report Modal */}
            {showReportModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: "white",
                        padding: "30px",
                        borderRadius: "8px",
                        width: "90%",
                        maxWidth: "500px",
                        maxHeight: "90vh",
                        overflow: "auto"
                    }}>
                        <h2 style={{ marginTop: 0 }}>Report App</h2>
                        <form onSubmit={handleReportSubmit}>
                            <div style={{ marginBottom: "20px" }}>
                                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                                    Reason *
                                </label>
                                <select
                                    value={reportData.reason}
                                    onChange={(e) => setReportData({ ...reportData, reason: e.target.value })}
                                    style={{
                                        width: "100%",
                                        padding: "10px",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        fontSize: "14px"
                                    }}
                                    required
                                >
                                    <option value="">Select a reason</option>
                                    <option value="inappropriate_content">Inappropriate Content</option>
                                    <option value="malware">Malware or Security Issue</option>
                                    <option value="copyright">Copyright Violation</option>
                                    <option value="misleading">Misleading Information</option>
                                    <option value="privacy">Privacy Concerns</option>
                                    <option value="not_working">App Not Working</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div style={{ marginBottom: "20px" }}>
                                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                                    Description
                                </label>
                                <textarea
                                    value={reportData.description}
                                    onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                                    placeholder="Please provide more details about your report..."
                                    style={{
                                        width: "100%",
                                        minHeight: "120px",
                                        padding: "10px",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        fontSize: "14px",
                                        fontFamily: "inherit",
                                        resize: "vertical"
                                    }}
                                />
                            </div>

                            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowReportModal(false);
                                        setReportData({ reason: "", description: "" });
                                    }}
                                    style={{
                                        padding: "10px 20px",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        background: "white",
                                        cursor: "pointer"
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        padding: "10px 20px",
                                        border: "none",
                                        borderRadius: "4px",
                                        background: "#d32f2f",
                                        color: "white",
                                        cursor: "pointer"
                                    }}
                                >
                                    Submit Report
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Image Lightbox */}
            {lightboxOpen && app.screenshots_urls && (
                <ImageLightbox
                    images={app.screenshots_urls}
                    currentIndex={currentImageIndex}
                    onClose={closeLightbox}
                    onNext={nextImage}
                    onPrev={prevImage}
                />
            )}

        </main>
    );
}

export default SingleApp;